'use client';

import React, { createContext, ReactElement, useContext, useEffect, useMemo, useState } from 'react';

import Image from 'next/image';

import infoIcon from '@/assets/icons/Tooltip-info-Icon.png';
import ImageIconShipping from '@/assets/icons/truck-icon-1.svg';
import ImageIconShippingThree from '@/assets/icons/truck-icon-3.svg';
import { SVGProps } from '@/components/svg/SVGProps';
import Tooltip from '@/components/V2/Tooltip';
import Paragraph from '@/components/V2/Typography/Paragraph';
import { useWindowDimensions } from '@/helpers/hooks';
import cn from '@/helpers/utils/classNames';

type Place = 'any' | 'ezship-sidebar';

interface StepperContextProps {
  activeStep: number;
  totalSteps: number;
  flow?: 'before' | 'after';
  onStepChange?: (step: number) => void;
  place?: Place;
}

const StepperContext = createContext<StepperContextProps>({ activeStep: 0, totalSteps: 0, onStepChange: () => {} });

interface StepContextProps {
  stepNumber: number;
}

const StepContext = createContext<StepContextProps>({ stepNumber: 0 });

interface StepperProps {
  children: Array<ReactElement<typeof Step>>;
  className?: string;
  activeStep: number;
  flow?: 'before' | 'after';
  place?: Place;
  totalSteps?: number;
  isEzShip?: boolean;
}

const StepperContainer = ({ children, isEzShip, className, activeStep, flow, place, totalSteps }: StepperProps) => {
  const [currentActiveStep, setCurrentActiveStep] = useState<number>(0);
  const dimensions = useWindowDimensions();
  const handleStepChange = (step: number) => setCurrentActiveStep(step);

  const freeShippingMessage = useMemo(() => {
    switch (activeStep) {
      case 1:
        return (
          <span className="v2-text-black v2-font-sweet-sans-pro v2-text-base v2-font-normal v2-text-center">
            Add 2 qualifying items on EZ-Ship for{' '}
            <span className="v2-font-sweet-sans-pro-semi-bold v2-text-[#154734]">FREE SHIPPING!!</span>
          </span>
        );
      case 2:
        return (
          <span className="v2-text-black v2-font-sweet-sans-pro v2-text-base v2-font-normal v2-text-center">
            Add 1 qualifying items on EZ-Ship for{' '}
            <span className="v2-font-sweet-sans-pro-semi-bold v2-text-[#154734]">FREE SHIPPING!!</span>
          </span>
        );
      case 3:
        return (
          <span className="v2-text-black v2-font-sweet-sans-pro v2-text-base v2-font-normal v2-text-center">
            Great News! <span className="v2-font-sweet-sans-pro-semi-bold v2-text-[#154734]">FREE SHIPPING</span> is now
            applied to your order!
          </span>
        );
      default:
        return (
          <span className="v2-text-black v2-font-sweet-sans-pro v2-text-base v2-font-normal v2-text-center">
            Add 3 qualifying items on EZ-Ship for{' '}
            <span className="v2-font-sweet-sans-pro-semi-bold v2-text-[#154734]">FREE SHIPPING!!</span>
          </span>
        );
    }
  }, [activeStep]);

  useEffect(() => {
    if (activeStep <= children.length && activeStep >= 0) setCurrentActiveStep(activeStep);
  }, [activeStep, children.length]);

  const isMobileView = useMemo(() => {
    return !!(dimensions.width <= 768);
  }, [dimensions]);

  return (
    <StepperContext.Provider
      value={{
        activeStep: currentActiveStep,
        totalSteps: children.length,
        onStepChange: handleStepChange,
        flow,
        place,
      }}
    >
      <div className="v2-rounded-xs v2-p-4 v2-bg-[#F3F6F5]">
        <div className={cn('v2-flex v2-justify-center', { 'v2-justify-end': isEzShip })}>
          {freeShippingMessage}
          <Tooltip
            placement={isMobileView ? 'top' : 'left'}
            variant="light"
            wrapperClassName="!v2-inline-block"
            mainContainerClassName="v2-inline-block v2-top-[3px]"
            title={
              <div className="v2-py-3 v2-px-3">
                <Paragraph
                  className="v2-mb-[8px] sm:v2-text-xs v2-font-sweet-sans-pro-medium  md:v2-font-sweet-sans-pro-medium md:v2-text-xs lg:v2-font-sweet-sans-pro-medium lg:v2-text-xs xs:v2-font-sweet-sans-pro-medium xs:v2-text-xs xxs:v2-font-sweet-sans-pro-medium xxs:v2-text-xs"
                  variant="gray"
                >
                  Add 3 qualifying items to an EZSHIP to receive FREE shipping!
                </Paragraph>
                <div className="v2-mb-[8px] v2-flex v2-max-w-[12rem] v2-justify-start v2-items-center v2-gap-2">
                  <Image src={ImageIconShipping} className="v2-h-[18px] v2-w-[15px]" alt="Shipping One" />
                  <Paragraph
                    className="v2-text-left v2-font-normal sm:v2-text-xs md:v2-text-xs lg:v2-text-xs xs:v2-text-xs xxs:v2-text-xs"
                    variant="gray"
                  >
                    This is a qualifying item!
                  </Paragraph>
                </div>
                <div className="v2-mb-[8px] v2-flex v2-max-w-[12rem] v2-justify-start v2-items-center v2-gap-2">
                  <Image src={ImageIconShippingThree} className="v2-h-[18px] v2-w-[15px]" alt="Shipping Three" />
                  <Paragraph
                    className="v2-text-left v2-font-normal sm:v2-text-xs md:v2-text-xs lg:v2-text-xs xs:v2-text-xs xxs:v2-text-xs"
                    variant="gray"
                  >
                    This is an item that counts as 3 or more qualifying items!
                  </Paragraph>
                </div>
                <Paragraph
                  className="v2-font-sweet-sans-pro v2-font-normal sm:v2-text-xs md:v2-text-xs lg:v2-text-xs xs:v2-text-xs xxs:v2-text-xs"
                  variant="gray"
                >
                  *Free shipping is a benefit available to all Paw Club members as long as they have 3 or more
                  Qualifying Items in their EZ Ship order. Qualifying Items include anything except kibble and canned
                  cat food. FREE SHIPPING Excludes Alaska, Hawaii and products purchased with pawPoints.
                </Paragraph>
              </div>
            }
          >
            <Image src={infoIcon} width={20} height={20} alt="info" className="v2-cursor-pointer ml-1" />
          </Tooltip>
        </div>
        <div className="v2-grid v2-grid-cols-3 v2-items-end v2-justify-center">
          {children?.map((child, index) => {
            const { type: Child, props } = child;
            return (
              <StepContext.Provider key={index + 1} value={{ stepNumber: index + 1 }}>
                <Child stepNumber={index + 1} {...props} />
              </StepContext.Provider>
            );
          })}
        </div>
        <div className="v2-grid v2-grid-cols-2 v2-mt-[1px]">
          <div className={cn('v2-w-full v2-h-[3px] v2-bg-[#98A2B3]', { '!v2-bg-[#154734]': activeStep > 1 })} />
          <div className={cn('v2-w-full v2-h-[3px] v2-bg-[#98A2B3]', { '!v2-bg-[#154734]': activeStep > 2 })} />
        </div>
      </div>
    </StepperContext.Provider>
  );
};

type StepIconCallback = (isStepActive: boolean) => ReactElement;

interface StepProps {
  icon: StepIconCallback | React.JSXElementConstructor<SVGProps> | ReactElement;
  className?: string;
  flow?: 'before' | 'after';
  lineColor?: string;
}

const isReactElement = (p: unknown): p is ReactElement => {
  return (p as ReactElement) !== undefined && (p as ReactElement).type !== undefined;
};

function isReactComponent(value: any): value is React.ComponentType<any> {
  return typeof value === 'function' && (value.prototype instanceof React.Component || 'isReactComponent' in value);
}

const Step = ({ icon: Icon, className, lineColor }: StepProps) => {
  const { activeStep } = useContext(StepperContext);
  const { stepNumber } = useContext(StepContext);

  const isStepActive = activeStep >= stepNumber;

  return (
    <div>
      {isReactElement(Icon) ? (
        Icon
      ) : isReactComponent(Icon) ? (
        <Icon fill={isStepActive ? '#79C200' : 'black'} width="40" height="35" />
      ) : (
        (Icon as StepIconCallback)(isStepActive)
      )}
    </div>
  );
};

export const useStepper = (currentStep: number, totalSteps: number) => {
  const [activeStep, setActiveStep] = useState<number>(0);

  const isFirstStep = activeStep === 1;
  const isLastStep = activeStep === totalSteps;

  const onNextStep = () => {
    if (activeStep < totalSteps) setActiveStep(prev => prev + 1);
  };

  const onPreviousStep = () => {
    if (activeStep > 0) setActiveStep(prev => prev - 1);
  };

  const setCurrentActiveStep = (step: number) => {
    if (activeStep >= 0 && activeStep <= totalSteps) setActiveStep(step);
  };

  useEffect(() => {
    setActiveStep(currentStep);
  }, [currentStep]);

  return { activeStep, onNextStep, onPreviousStep, setCurrentActiveStep, isFirstStep, isLastStep, totalSteps };
};

Step.displayName = 'Step';
StepperContainer.Step = Step;

export default StepperContainer;
